import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserRole } from 'src/enums/User';
import { ProgressReportDataType, RootData } from 'src/interfaces/ProgressReport';
import { useAppSelector } from 'src/redux/redux-store/hooks';
import { parentService, ResponseKind } from 'src/services/services';
import { transformToProgressReport } from 'src/utils/helpers';
import useChildrenData from './useChildrenData';

const useProgressReport = () => {
  const { selectedChild, activeSubject, children } = useAppSelector(
    (state) => state.child
  );

  const [progressReportData, setProgressReportData] =
    useState<ProgressReportDataType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redux user info
  const userRoleFromRedux = useAppSelector((state) => state.user.role);
  const userIDFromRedux = useAppSelector((state) => state.user.id);
  const userLevelFromRedux = useAppSelector((state) => state.user.level);
  const subjectFromRedux = useAppSelector((state) => state.chat.activeSubject);

  const { getChildren, setSelectedChild } = useChildrenData();
  const [searchParams] = useSearchParams();
  const searchChild = searchParams.get('child');

  const hasFetchedChildren = useRef(false);
  const lastFetchedKey = useRef<string | null>(null);

  /** --- Fetch children on mount if needed --- */
  useEffect(() => {
    if (!hasFetchedChildren.current && (!children || children.length === 0)) {
      getChildren();
      hasFetchedChildren.current = true;
    }
  }, [children, getChildren]);

  /** --- Sync URL param child with redux selectedChild --- */
  useEffect(() => {
    if (searchChild && searchChild !== selectedChild) {
      setSelectedChild(searchChild);
    }
  }, [searchChild, selectedChild, setSelectedChild]);

  /** --- Generalized progress report fetcher --- */
  const getProgressReport = useCallback(
    async (childId: number, subject: string, level: string) => {
      const fetchKey = `${childId}-${subject}`;
      if (lastFetchedKey.current === fetchKey) return;
      lastFetchedKey.current = fetchKey;

      setLoading(true);
      setError(null);

      try {
        const res = await parentService.getChildProgress(
          Number(childId),
          subject,
          level
        );

        if (res.kind === ResponseKind.OK) {
          if (res.data && !(res.data as any).detail) {
            const transformedData = transformToProgressReport(res.data as RootData);
            console.log('transformedDatax : ', transformedData);
            setProgressReportData(transformedData);
          } else {
            setProgressReportData(null);
          }
        } else {
          const errorMsg =
            'errors' in res && res.errors
              ? res.errors
              : 'Failed to fetch progress report';
          throw new Error(errorMsg);
        }
      } catch (error) {
        console.error('Error fetching progress report:', error);
        setError(
          error instanceof Error
            ? error.message
            : 'Failed to fetch progress report'
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /** --- Decide which data to use depending on role --- */
  useEffect(() => {
    console.log('DATA THAT PREV ', selectedChild, activeSubject, children);

    // 🧩 Admin or parent case
    if (
      userRoleFromRedux !== UserRole.user &&
      selectedChild &&
      activeSubject &&
      children?.length
    ) {
      const childObj = children.find(
        (child) => child.username === selectedChild
      );
      if (childObj) {
        getProgressReport(childObj.id, activeSubject, childObj.level);
      }
    }

    // 👤 Regular user case
    else if (userRoleFromRedux === UserRole.user) {
      if (userIDFromRedux && subjectFromRedux && userLevelFromRedux) {
        getProgressReport(
          Number(userIDFromRedux),
          subjectFromRedux,
          userLevelFromRedux
        );
      }
    }
  }, [
    selectedChild,
    activeSubject,
    children,
    userRoleFromRedux,
    userIDFromRedux,
    userLevelFromRedux,
    subjectFromRedux,
    getProgressReport,
  ]);

  return {
    progressReportData,
    loading,
    error,
    getProgressReport, // now reusable with params
  };
};

export default useProgressReport;
